import { useIntl } from 'react-intl';
import Select from '@alltrails/denali/components/Select';
import type CountryCode from '@alltrails/types/CountryCode';

type BillingCountrySelectorProps = {
  billingCountryCode: string;
  onChangeBillingCountry: (countryCode: CountryCode) => void;
  className?: string;
  labelText?: string;
};

const euCountryCodes = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE'
];

export const isBillingCountryEu = (billingCountryCode: CountryCode) => euCountryCodes.includes(billingCountryCode);

const BillingCountrySelector = ({ billingCountryCode, onChangeBillingCountry, className, labelText }: BillingCountrySelectorProps) => {
  const intl = useIntl();
  const countryOptions = [
    // To match server we use countries from https://github.com/countries/countries
    { label: intl.formatMessage({ defaultMessage: 'Afghanistan' }), value: 'AF' },
    { label: intl.formatMessage({ defaultMessage: 'Albania' }), value: 'AL' },
    { label: intl.formatMessage({ defaultMessage: 'Algeria' }), value: 'DZ' },
    { label: intl.formatMessage({ defaultMessage: 'American Samoa' }), value: 'AS' },
    { label: intl.formatMessage({ defaultMessage: 'Andorra' }), value: 'AD' },
    { label: intl.formatMessage({ defaultMessage: 'Angola' }), value: 'AO' },
    { label: intl.formatMessage({ defaultMessage: 'Anguilla' }), value: 'AI' },
    { label: intl.formatMessage({ defaultMessage: 'Antarctica' }), value: 'AQ' },
    { label: intl.formatMessage({ defaultMessage: 'Antigua and Barbuda' }), value: 'AG' },
    { label: intl.formatMessage({ defaultMessage: 'Argentina' }), value: 'AR' },
    { label: intl.formatMessage({ defaultMessage: 'Armenia' }), value: 'AM' },
    { label: intl.formatMessage({ defaultMessage: 'Aruba' }), value: 'AW' },
    { label: intl.formatMessage({ defaultMessage: 'Australia' }), value: 'AU' },
    { label: intl.formatMessage({ defaultMessage: 'Austria' }), value: 'AT' },
    { label: intl.formatMessage({ defaultMessage: 'Azerbaijan' }), value: 'AZ' },
    { label: intl.formatMessage({ defaultMessage: 'Bahamas' }), value: 'BS' },
    { label: intl.formatMessage({ defaultMessage: 'Bahrain' }), value: 'BH' },
    { label: intl.formatMessage({ defaultMessage: 'Bangladesh' }), value: 'BD' },
    { label: intl.formatMessage({ defaultMessage: 'Barbados' }), value: 'BB' },
    { label: intl.formatMessage({ defaultMessage: 'Belarus' }), value: 'BY' },
    { label: intl.formatMessage({ defaultMessage: 'Belgium' }), value: 'BE' },
    { label: intl.formatMessage({ defaultMessage: 'Belize' }), value: 'BZ' },
    { label: intl.formatMessage({ defaultMessage: 'Benin' }), value: 'BJ' },
    { label: intl.formatMessage({ defaultMessage: 'Bermuda' }), value: 'BM' },
    { label: intl.formatMessage({ defaultMessage: 'Bhutan' }), value: 'BT' },
    { label: intl.formatMessage({ defaultMessage: 'Bolivia' }), value: 'BO' },
    { label: intl.formatMessage({ defaultMessage: 'Bonaire, Sint Eustatius and Saba' }), value: 'BQ' },
    { label: intl.formatMessage({ defaultMessage: 'Bosnia and Herzegovina' }), value: 'BA' },
    { label: intl.formatMessage({ defaultMessage: 'Botswana' }), value: 'BW' },
    { label: intl.formatMessage({ defaultMessage: 'Bouvet Island' }), value: 'BV' },
    { label: intl.formatMessage({ defaultMessage: 'Brazil' }), value: 'BR' },
    { label: intl.formatMessage({ defaultMessage: 'British Indian Ocean Territory' }), value: 'IO' },
    { label: intl.formatMessage({ defaultMessage: 'Brunei Darussalam' }), value: 'BN' },
    { label: intl.formatMessage({ defaultMessage: 'Bulgaria' }), value: 'BG' },
    { label: intl.formatMessage({ defaultMessage: 'Burkina Faso' }), value: 'BF' },
    { label: intl.formatMessage({ defaultMessage: 'Burundi' }), value: 'BI' },
    { label: intl.formatMessage({ defaultMessage: 'Cabo Verde' }), value: 'CV' },
    { label: intl.formatMessage({ defaultMessage: 'Cambodia' }), value: 'KH' },
    { label: intl.formatMessage({ defaultMessage: 'Cameroon' }), value: 'CM' },
    { label: intl.formatMessage({ defaultMessage: 'Canada' }), value: 'CA' },
    { label: intl.formatMessage({ defaultMessage: 'Cayman Islands' }), value: 'KY' },
    { label: intl.formatMessage({ defaultMessage: 'Central African Republic' }), value: 'CF' },
    { label: intl.formatMessage({ defaultMessage: 'Chad' }), value: 'TD' },
    { label: intl.formatMessage({ defaultMessage: 'Chile' }), value: 'CL' },
    { label: intl.formatMessage({ defaultMessage: 'China' }), value: 'CN' },
    { label: intl.formatMessage({ defaultMessage: 'Christmas Island' }), value: 'CX' },
    { label: intl.formatMessage({ defaultMessage: 'Cocos (Keeling) Islands' }), value: 'CC' },
    { label: intl.formatMessage({ defaultMessage: 'Colombia' }), value: 'CO' },
    { label: intl.formatMessage({ defaultMessage: 'Comoros' }), value: 'KM' },
    { label: intl.formatMessage({ defaultMessage: 'Congo' }), value: 'CG' },
    { label: intl.formatMessage({ defaultMessage: 'Congo, The Democratic Republic of the' }), value: 'CD' },
    { label: intl.formatMessage({ defaultMessage: 'Cook Islands' }), value: 'CK' },
    { label: intl.formatMessage({ defaultMessage: 'Costa Rica' }), value: 'CR' },
    { label: intl.formatMessage({ defaultMessage: 'Croatia' }), value: 'HR' },
    { label: intl.formatMessage({ defaultMessage: 'Cuba' }), value: 'CU' },
    { label: intl.formatMessage({ defaultMessage: 'Curaçao' }), value: 'CW' },
    { label: intl.formatMessage({ defaultMessage: 'Cyprus' }), value: 'CY' },
    { label: intl.formatMessage({ defaultMessage: 'Czechia' }), value: 'CZ' },
    { label: intl.formatMessage({ defaultMessage: "Côte d'Ivoire" }), value: 'CI' },
    { label: intl.formatMessage({ defaultMessage: 'Denmark' }), value: 'DK' },
    { label: intl.formatMessage({ defaultMessage: 'Djibouti' }), value: 'DJ' },
    { label: intl.formatMessage({ defaultMessage: 'Dominica' }), value: 'DM' },
    { label: intl.formatMessage({ defaultMessage: 'Dominican Republic' }), value: 'DO' },
    { label: intl.formatMessage({ defaultMessage: 'Ecuador' }), value: 'EC' },
    { label: intl.formatMessage({ defaultMessage: 'Egypt' }), value: 'EG' },
    { label: intl.formatMessage({ defaultMessage: 'El Salvador' }), value: 'SV' },
    { label: intl.formatMessage({ defaultMessage: 'Equatorial Guinea' }), value: 'GQ' },
    { label: intl.formatMessage({ defaultMessage: 'Eritrea' }), value: 'ER' },
    { label: intl.formatMessage({ defaultMessage: 'Estonia' }), value: 'EE' },
    { label: intl.formatMessage({ defaultMessage: 'Eswatini' }), value: 'SZ' },
    { label: intl.formatMessage({ defaultMessage: 'Ethiopia' }), value: 'ET' },
    { label: intl.formatMessage({ defaultMessage: 'Falkland Islands (Malvinas)' }), value: 'FK' },
    { label: intl.formatMessage({ defaultMessage: 'Faroe Islands' }), value: 'FO' },
    { label: intl.formatMessage({ defaultMessage: 'Fiji' }), value: 'FJ' },
    { label: intl.formatMessage({ defaultMessage: 'Finland' }), value: 'FI' },
    { label: intl.formatMessage({ defaultMessage: 'France' }), value: 'FR' },
    { label: intl.formatMessage({ defaultMessage: 'French Guiana' }), value: 'GF' },
    { label: intl.formatMessage({ defaultMessage: 'French Polynesia' }), value: 'PF' },
    { label: intl.formatMessage({ defaultMessage: 'French Southern Territories' }), value: 'TF' },
    { label: intl.formatMessage({ defaultMessage: 'Gabon' }), value: 'GA' },
    { label: intl.formatMessage({ defaultMessage: 'Gambia' }), value: 'GM' },
    { label: intl.formatMessage({ defaultMessage: 'Georgia' }), value: 'GE' },
    { label: intl.formatMessage({ defaultMessage: 'Germany' }), value: 'DE' },
    { label: intl.formatMessage({ defaultMessage: 'Ghana' }), value: 'GH' },
    { label: intl.formatMessage({ defaultMessage: 'Gibraltar' }), value: 'GI' },
    { label: intl.formatMessage({ defaultMessage: 'Greece' }), value: 'GR' },
    { label: intl.formatMessage({ defaultMessage: 'Greenland' }), value: 'GL' },
    { label: intl.formatMessage({ defaultMessage: 'Grenada' }), value: 'GD' },
    { label: intl.formatMessage({ defaultMessage: 'Guadeloupe' }), value: 'GP' },
    { label: intl.formatMessage({ defaultMessage: 'Guam' }), value: 'GU' },
    { label: intl.formatMessage({ defaultMessage: 'Guatemala' }), value: 'GT' },
    { label: intl.formatMessage({ defaultMessage: 'Guernsey' }), value: 'GG' },
    { label: intl.formatMessage({ defaultMessage: 'Guinea' }), value: 'GN' },
    { label: intl.formatMessage({ defaultMessage: 'Guinea-Bissau' }), value: 'GW' },
    { label: intl.formatMessage({ defaultMessage: 'Guyana' }), value: 'GY' },
    { label: intl.formatMessage({ defaultMessage: 'Haiti' }), value: 'HT' },
    { label: intl.formatMessage({ defaultMessage: 'Heard Island and McDonald Islands' }), value: 'HM' },
    { label: intl.formatMessage({ defaultMessage: 'Holy See (Vatican City State)' }), value: 'VA' },
    { label: intl.formatMessage({ defaultMessage: 'Honduras' }), value: 'HN' },
    { label: intl.formatMessage({ defaultMessage: 'Hong Kong' }), value: 'HK' },
    { label: intl.formatMessage({ defaultMessage: 'Hungary' }), value: 'HU' },
    { label: intl.formatMessage({ defaultMessage: 'Iceland' }), value: 'IS' },
    { label: intl.formatMessage({ defaultMessage: 'India' }), value: 'IN' },
    { label: intl.formatMessage({ defaultMessage: 'Indonesia' }), value: 'ID' },
    { label: intl.formatMessage({ defaultMessage: 'Iran' }), value: 'IR' },
    { label: intl.formatMessage({ defaultMessage: 'Iraq' }), value: 'IQ' },
    { label: intl.formatMessage({ defaultMessage: 'Ireland' }), value: 'IE' },
    { label: intl.formatMessage({ defaultMessage: 'Isle of Man' }), value: 'IM' },
    { label: intl.formatMessage({ defaultMessage: 'Israel' }), value: 'IL' },
    { label: intl.formatMessage({ defaultMessage: 'Italy' }), value: 'IT' },
    { label: intl.formatMessage({ defaultMessage: 'Jamaica' }), value: 'JM' },
    { label: intl.formatMessage({ defaultMessage: 'Japan' }), value: 'JP' },
    { label: intl.formatMessage({ defaultMessage: 'Jersey' }), value: 'JE' },
    { label: intl.formatMessage({ defaultMessage: 'Jordan' }), value: 'JO' },
    { label: intl.formatMessage({ defaultMessage: 'Kazakhstan' }), value: 'KZ' },
    { label: intl.formatMessage({ defaultMessage: 'Kenya' }), value: 'KE' },
    { label: intl.formatMessage({ defaultMessage: 'Kiribati' }), value: 'KI' },
    { label: intl.formatMessage({ defaultMessage: 'Kosovo' }), value: 'XK' },
    { label: intl.formatMessage({ defaultMessage: 'Kuwait' }), value: 'KW' },
    { label: intl.formatMessage({ defaultMessage: 'Kyrgyzstan' }), value: 'KG' },
    { label: intl.formatMessage({ defaultMessage: "Lao People's Democratic Republic" }), value: 'LA' },
    { label: intl.formatMessage({ defaultMessage: 'Latvia' }), value: 'LV' },
    { label: intl.formatMessage({ defaultMessage: 'Lebanon' }), value: 'LB' },
    { label: intl.formatMessage({ defaultMessage: 'Lesotho' }), value: 'LS' },
    { label: intl.formatMessage({ defaultMessage: 'Liberia' }), value: 'LR' },
    { label: intl.formatMessage({ defaultMessage: 'Libya' }), value: 'LY' },
    { label: intl.formatMessage({ defaultMessage: 'Liechtenstein' }), value: 'LI' },
    { label: intl.formatMessage({ defaultMessage: 'Lithuania' }), value: 'LT' },
    { label: intl.formatMessage({ defaultMessage: 'Luxembourg' }), value: 'LU' },
    { label: intl.formatMessage({ defaultMessage: 'Macao' }), value: 'MO' },
    { label: intl.formatMessage({ defaultMessage: 'Madagascar' }), value: 'MG' },
    { label: intl.formatMessage({ defaultMessage: 'Malawi' }), value: 'MW' },
    { label: intl.formatMessage({ defaultMessage: 'Malaysia' }), value: 'MY' },
    { label: intl.formatMessage({ defaultMessage: 'Maldives' }), value: 'MV' },
    { label: intl.formatMessage({ defaultMessage: 'Mali' }), value: 'ML' },
    { label: intl.formatMessage({ defaultMessage: 'Malta' }), value: 'MT' },
    { label: intl.formatMessage({ defaultMessage: 'Marshall Islands' }), value: 'MH' },
    { label: intl.formatMessage({ defaultMessage: 'Martinique' }), value: 'MQ' },
    { label: intl.formatMessage({ defaultMessage: 'Mauritania' }), value: 'MR' },
    { label: intl.formatMessage({ defaultMessage: 'Mauritius' }), value: 'MU' },
    { label: intl.formatMessage({ defaultMessage: 'Mayotte' }), value: 'YT' },
    { label: intl.formatMessage({ defaultMessage: 'Mexico' }), value: 'MX' },
    { label: intl.formatMessage({ defaultMessage: 'Micronesia, Federated States of' }), value: 'FM' },
    { label: intl.formatMessage({ defaultMessage: 'Moldova' }), value: 'MD' },
    { label: intl.formatMessage({ defaultMessage: 'Monaco' }), value: 'MC' },
    { label: intl.formatMessage({ defaultMessage: 'Mongolia' }), value: 'MN' },
    { label: intl.formatMessage({ defaultMessage: 'Montenegro' }), value: 'ME' },
    { label: intl.formatMessage({ defaultMessage: 'Montserrat' }), value: 'MS' },
    { label: intl.formatMessage({ defaultMessage: 'Morocco' }), value: 'MA' },
    { label: intl.formatMessage({ defaultMessage: 'Mozambique' }), value: 'MZ' },
    { label: intl.formatMessage({ defaultMessage: 'Myanmar' }), value: 'MM' },
    { label: intl.formatMessage({ defaultMessage: 'Namibia' }), value: 'NA' },
    { label: intl.formatMessage({ defaultMessage: 'Nauru' }), value: 'NR' },
    { label: intl.formatMessage({ defaultMessage: 'Nepal' }), value: 'NP' },
    { label: intl.formatMessage({ defaultMessage: 'Netherlands' }), value: 'NL' },
    { label: intl.formatMessage({ defaultMessage: 'New Caledonia' }), value: 'NC' },
    { label: intl.formatMessage({ defaultMessage: 'New Zealand' }), value: 'NZ' },
    { label: intl.formatMessage({ defaultMessage: 'Nicaragua' }), value: 'NI' },
    { label: intl.formatMessage({ defaultMessage: 'Niger' }), value: 'NE' },
    { label: intl.formatMessage({ defaultMessage: 'Nigeria' }), value: 'NG' },
    { label: intl.formatMessage({ defaultMessage: 'Niue' }), value: 'NU' },
    { label: intl.formatMessage({ defaultMessage: 'Norfolk Island' }), value: 'NF' },
    { label: intl.formatMessage({ defaultMessage: 'North Korea' }), value: 'KP' },
    { label: intl.formatMessage({ defaultMessage: 'North Macedonia' }), value: 'MK' },
    { label: intl.formatMessage({ defaultMessage: 'Northern Mariana Islands' }), value: 'MP' },
    { label: intl.formatMessage({ defaultMessage: 'Norway' }), value: 'NO' },
    { label: intl.formatMessage({ defaultMessage: 'Oman' }), value: 'OM' },
    { label: intl.formatMessage({ defaultMessage: 'Pakistan' }), value: 'PK' },
    { label: intl.formatMessage({ defaultMessage: 'Palau' }), value: 'PW' },
    { label: intl.formatMessage({ defaultMessage: 'Palestine, State of' }), value: 'PS' },
    { label: intl.formatMessage({ defaultMessage: 'Panama' }), value: 'PA' },
    { label: intl.formatMessage({ defaultMessage: 'Papua New Guinea' }), value: 'PG' },
    { label: intl.formatMessage({ defaultMessage: 'Paraguay' }), value: 'PY' },
    { label: intl.formatMessage({ defaultMessage: 'Peru' }), value: 'PE' },
    { label: intl.formatMessage({ defaultMessage: 'Philippines' }), value: 'PH' },
    { label: intl.formatMessage({ defaultMessage: 'Pitcairn' }), value: 'PN' },
    { label: intl.formatMessage({ defaultMessage: 'Poland' }), value: 'PL' },
    { label: intl.formatMessage({ defaultMessage: 'Portugal' }), value: 'PT' },
    { label: intl.formatMessage({ defaultMessage: 'Qatar' }), value: 'QA' },
    { label: intl.formatMessage({ defaultMessage: 'Romania' }), value: 'RO' },
    { label: intl.formatMessage({ defaultMessage: 'Russian Federation' }), value: 'RU' },
    { label: intl.formatMessage({ defaultMessage: 'Rwanda' }), value: 'RW' },
    { label: intl.formatMessage({ defaultMessage: 'Réunion' }), value: 'RE' },
    { label: intl.formatMessage({ defaultMessage: 'Saint Barthélemy' }), value: 'BL' },
    { label: intl.formatMessage({ defaultMessage: 'Saint Helena, Ascension and Tristan da Cunha' }), value: 'SH' },
    { label: intl.formatMessage({ defaultMessage: 'Saint Kitts and Nevis' }), value: 'KN' },
    { label: intl.formatMessage({ defaultMessage: 'Saint Lucia' }), value: 'LC' },
    { label: intl.formatMessage({ defaultMessage: 'Saint Martin (French part)' }), value: 'MF' },
    { label: intl.formatMessage({ defaultMessage: 'Saint Pierre and Miquelon' }), value: 'PM' },
    { label: intl.formatMessage({ defaultMessage: 'Saint Vincent and the Grenadines' }), value: 'VC' },
    { label: intl.formatMessage({ defaultMessage: 'Samoa' }), value: 'WS' },
    { label: intl.formatMessage({ defaultMessage: 'San Marino' }), value: 'SM' },
    { label: intl.formatMessage({ defaultMessage: 'Sao Tome and Principe' }), value: 'ST' },
    { label: intl.formatMessage({ defaultMessage: 'Saudi Arabia' }), value: 'SA' },
    { label: intl.formatMessage({ defaultMessage: 'Senegal' }), value: 'SN' },
    { label: intl.formatMessage({ defaultMessage: 'Serbia' }), value: 'RS' },
    { label: intl.formatMessage({ defaultMessage: 'Seychelles' }), value: 'SC' },
    { label: intl.formatMessage({ defaultMessage: 'Sierra Leone' }), value: 'SL' },
    { label: intl.formatMessage({ defaultMessage: 'Singapore' }), value: 'SG' },
    { label: intl.formatMessage({ defaultMessage: 'Sint Maarten (Dutch part)' }), value: 'SX' },
    { label: intl.formatMessage({ defaultMessage: 'Slovakia' }), value: 'SK' },
    { label: intl.formatMessage({ defaultMessage: 'Slovenia' }), value: 'SI' },
    { label: intl.formatMessage({ defaultMessage: 'Solomon Islands' }), value: 'SB' },
    { label: intl.formatMessage({ defaultMessage: 'Somalia' }), value: 'SO' },
    { label: intl.formatMessage({ defaultMessage: 'South Africa' }), value: 'ZA' },
    { label: intl.formatMessage({ defaultMessage: 'South Georgia and the South Sandwich Islands' }), value: 'GS' },
    { label: intl.formatMessage({ defaultMessage: 'South Korea' }), value: 'KR' },
    { label: intl.formatMessage({ defaultMessage: 'South Sudan' }), value: 'SS' },
    { label: intl.formatMessage({ defaultMessage: 'Spain' }), value: 'ES' },
    { label: intl.formatMessage({ defaultMessage: 'Sri Lanka' }), value: 'LK' },
    { label: intl.formatMessage({ defaultMessage: 'Sudan' }), value: 'SD' },
    { label: intl.formatMessage({ defaultMessage: 'Suriname' }), value: 'SR' },
    { label: intl.formatMessage({ defaultMessage: 'Svalbard and Jan Mayen' }), value: 'SJ' },
    { label: intl.formatMessage({ defaultMessage: 'Sweden' }), value: 'SE' },
    { label: intl.formatMessage({ defaultMessage: 'Switzerland' }), value: 'CH' },
    { label: intl.formatMessage({ defaultMessage: 'Syrian Arab Republic' }), value: 'SY' },
    { label: intl.formatMessage({ defaultMessage: 'Taiwan' }), value: 'TW' },
    { label: intl.formatMessage({ defaultMessage: 'Tajikistan' }), value: 'TJ' },
    { label: intl.formatMessage({ defaultMessage: 'Tanzania' }), value: 'TZ' },
    { label: intl.formatMessage({ defaultMessage: 'Thailand' }), value: 'TH' },
    { label: intl.formatMessage({ defaultMessage: 'Timor-Leste' }), value: 'TL' },
    { label: intl.formatMessage({ defaultMessage: 'Togo' }), value: 'TG' },
    { label: intl.formatMessage({ defaultMessage: 'Tokelau' }), value: 'TK' },
    { label: intl.formatMessage({ defaultMessage: 'Tonga' }), value: 'TO' },
    { label: intl.formatMessage({ defaultMessage: 'Trinidad and Tobago' }), value: 'TT' },
    { label: intl.formatMessage({ defaultMessage: 'Tunisia' }), value: 'TN' },
    { label: intl.formatMessage({ defaultMessage: 'Turkmenistan' }), value: 'TM' },
    { label: intl.formatMessage({ defaultMessage: 'Turks and Caicos Islands' }), value: 'TC' },
    { label: intl.formatMessage({ defaultMessage: 'Tuvalu' }), value: 'TV' },
    { label: intl.formatMessage({ defaultMessage: 'Türkiye' }), value: 'TR' },
    { label: intl.formatMessage({ defaultMessage: 'Uganda' }), value: 'UG' },
    { label: intl.formatMessage({ defaultMessage: 'Ukraine' }), value: 'UA' },
    { label: intl.formatMessage({ defaultMessage: 'United Arab Emirates' }), value: 'AE' },
    { label: intl.formatMessage({ defaultMessage: 'United Kingdom' }), value: 'GB' },
    { label: intl.formatMessage({ defaultMessage: 'United States' }), value: 'US' },
    { label: intl.formatMessage({ defaultMessage: 'United States Minor Outlying Islands' }), value: 'UM' },
    { label: intl.formatMessage({ defaultMessage: 'Uruguay' }), value: 'UY' },
    { label: intl.formatMessage({ defaultMessage: 'Uzbekistan' }), value: 'UZ' },
    { label: intl.formatMessage({ defaultMessage: 'Vanuatu' }), value: 'VU' },
    { label: intl.formatMessage({ defaultMessage: 'Venezuela' }), value: 'VE' },
    { label: intl.formatMessage({ defaultMessage: 'Vietnam' }), value: 'VN' },
    { label: intl.formatMessage({ defaultMessage: 'Virgin Islands, British' }), value: 'VG' },
    { label: intl.formatMessage({ defaultMessage: 'Virgin Islands, U.S.' }), value: 'VI' },
    { label: intl.formatMessage({ defaultMessage: 'Wallis and Futuna' }), value: 'WF' },
    { label: intl.formatMessage({ defaultMessage: 'Western Sahara' }), value: 'EH' },
    { label: intl.formatMessage({ defaultMessage: 'Yemen' }), value: 'YE' },
    { label: intl.formatMessage({ defaultMessage: 'Zambia' }), value: 'ZM' },
    { label: intl.formatMessage({ defaultMessage: 'Zimbabwe' }), value: 'ZW' },
    { label: intl.formatMessage({ defaultMessage: 'Åland Islands' }), value: 'AX' }
  ];

  const selectedOption = countryOptions.find(option => option.value === billingCountryCode);
  const billingCountryText = intl.formatMessage({ defaultMessage: 'Billing country' });

  const defaultLabelText = selectedOption
    ? intl.formatMessage({ defaultMessage: 'Not in {countryName}? Update your location' }, { countryName: selectedOption.label })
    : billingCountryText;

  return (
    <Select
      labelTextColor="tertiary"
      labelText={labelText || defaultLabelText}
      onChange={value => onChangeBillingCountry(value as CountryCode)}
      options={countryOptions}
      placeholder={billingCountryText}
      value={billingCountryCode}
      className={className}
    />
  );
};

export default BillingCountrySelector;
